import { useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import moment from 'moment';

import Button from '../../../components/Buttons/Button';

import { maxWindowEnd, recommendedWindow } from './useWatchdog';
import {
	emailFinePrint,
	formatDateRange,
	intro,
	MONITORING_MAX_DAYS,
	sweepBlockBody,
	WATCHDOG_COPY,
} from './watchdogCopy';
import { StatusChip } from './WatchdogUi';

import 'react-datepicker/dist/react-datepicker.css';
import styles from './Watchdog.module.scss';

type WatchdogStartType = {
	destination: string;
	email?: string | null;
	travelDateFrom: string | null;
	travelDateTo: string | null;
	onStart: (_window: { start: Date; end: Date }) => void;
};

/** State C (§4): the one place a traveler learns what the Watchdog is. */
export default function WatchdogStart(props: WatchdogStartType) {
	const { destination, email, travelDateFrom, travelDateTo, onStart } = props;

	const recommended = useMemo(
		() => recommendedWindow(travelDateFrom, travelDateTo),
		[travelDateFrom, travelDateTo],
	);

	const [start, setStart] = useState<Date | null>(recommended?.start || null);
	const [end, setEnd] = useState<Date | null>(recommended?.end || null);

	const today = moment().startOf('day').toDate();
	const lengthInDays = start && end ? moment(end).diff(moment(start), 'days') + 1 : 0;
	const isValid = lengthInDays >= 1 && lengthInDays <= MONITORING_MAX_DAYS;

	const applyRecommended = () => {
		if (!recommended) {
			return;
		}
		setStart(recommended.start);
		setEnd(recommended.end);
	};

	return (
		<section className={styles.panel}>
			<StatusChip label={WATCHDOG_COPY.notMonitoringChip} tone="idle" />
			<h3 className={styles['panel-headline']}>{WATCHDOG_COPY.startHeadline}</h3>
			<p className={styles['panel-body']}>{intro(destination)}</p>

			<div className={styles.steps}>
				{WATCHDOG_COPY.howItWorks.map((step, index) => (
					<div className={styles.step} key={step.title}>
						<span className={`material-icons ${styles['step-icon']}`}>{step.icon}</span>
						<h4 className={styles['step-title']}>{step.title}</h4>
						<p className={styles['step-body']}>
							{index === 1 ? sweepBlockBody(destination) : step.body}
						</p>
					</div>
				))}
			</div>

			<div className={styles.window}>
				<span className={styles['window-label']}>{WATCHDOG_COPY.monitoringWindowLabel}</span>
				<p className={styles['window-helper']}>{WATCHDOG_COPY.monitoringWindowHelper}</p>

				{recommended && (
					<button type="button" className={styles['preset-chip']} onClick={applyRecommended}>
						{`Recommended: ${formatDateRange(recommended.start, recommended.end)}`}
					</button>
				)}

				<div className={styles['window-fields']}>
					<label htmlFor="watchdog-window-start" className={styles['window-field']}>
						Start
						<DatePicker
							id="watchdog-window-start"
							selected={start}
							onChange={(date: Date | null) => {
								const next = date ? moment(date).startOf('day').toDate() : null;
								setStart(next);
								if (next && end && moment(end).isBefore(next)) {
									setEnd(next);
								}
							}}
							selectsStart
							startDate={start}
							endDate={end}
							minDate={today}
							dateFormat="MMM d, yyyy"
							placeholderText="Select start"
							className={styles['window-input']}
							wrapperClassName={styles['window-picker']}
							popperClassName={styles['window-popper']}
							showPopperArrow={false}
							showIcon
							calendarIconClassName={styles['window-icon']}
						/>
					</label>
					<label htmlFor="watchdog-window-end" className={styles['window-field']}>
						End
						<DatePicker
							id="watchdog-window-end"
							selected={end}
							onChange={(date: Date | null) =>
								setEnd(date ? moment(date).startOf('day').toDate() : null)
							}
							selectsEnd
							startDate={start}
							endDate={end}
							minDate={start || today}
							maxDate={maxWindowEnd(start)}
							dateFormat="MMM d, yyyy"
							placeholderText="Select end"
							className={styles['window-input']}
							wrapperClassName={styles['window-picker']}
							popperClassName={styles['window-popper']}
							showPopperArrow={false}
							showIcon
							calendarIconClassName={styles['window-icon']}
						/>
					</label>
				</div>

				{!isValid && start && end && (
					<p className={styles['window-error']}>
						{`Monitoring runs for 1 to ${MONITORING_MAX_DAYS} days. Adjust the window to continue.`}
					</p>
				)}
			</div>

			<div className={styles['panel-actions']}>
				<Button
					variant="filled"
					color="primary"
					disabled={!isValid}
					onClick={() => start && end && onStart({ start, end })}
				>
					{WATCHDOG_COPY.startCta}
				</Button>
			</div>
			<p className={styles['fine-print']}>{emailFinePrint(email)}</p>
		</section>
	);
}
